// The confirm card for an action on an Ask list (steps 4.11, 4.12). Ask proposes, the person checks who is included
// and who is left out and why, and confirms. An email campaign then goes to the outreach agent to write, and every
// email waits on Home (gate 4.10): nothing is sent from here. Other actions run row by row through act.
import { useEffect, useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CircleCheck, Wand } from "lucide-react";
import { IconTile } from "./kit";
import "./ActionCard.css";
import type { Crm } from "../lib/crm";
import type { AskSpec } from "../lib/ask";
import {
  ACTION_KINDS,
  ACTION_LABELS,
  groupExclusions,
  missing,
  whatWillHappen,
  type ActionKind,
  type ActionParams,
  type ActionPreview,
  type ActionReply,
  type BulkResult,
  type CampaignCreated,
} from "../lib/askActions";
import { NATION_LABELS, type Nation } from "../lib/nation";
import { fmtNumber } from "../lib/format";
import type { ContactTag, Stage } from "../lib/types";
import { Notice } from "./ui";
import { BULK_EMAILS_PATH, FIND_CAMPAIGNS_PATH, emailRoute, emailRouteText, sampleFirms, type EmailRoute } from "../lib/bulkEmails";

type Done = { kind: "campaign"; result: CampaignCreated } | { kind: "bulk"; result: BulkResult };

/** The parameters that change who is included, so a change re-runs the preview. */
function previewKey(action: ActionKind, p: ActionParams): string {
  const pick = { email: ["all_contacts"], tag_people: ["add", "remove", "all_contacts"], enrol_in_sequence: ["sequence", "all_contacts"],
    create_tasks: ["per"], set_stage: ["stage"], link_introducer: ["introducer", "all_contacts"] }[action];
  return JSON.stringify(pick.map((k) => p[k] ?? null));
}

export function ActionCard({ crm, spec, initial, onClose }: { crm: Crm; spec: AskSpec; initial: ActionReply; onClose: () => void }) {
  const id = useId();
  const [action, setAction] = useState<ActionKind>(initial.action);
  const [params, setParams] = useState<ActionParams>(initial.params ?? {});
  const [name, setName] = useState(initial.name ?? "");
  const [brief, setBrief] = useState(initial.brief ?? "");
  const [preview, setPreview] = useState<ActionPreview | null>(initial.preview);
  const [previewError, setPreviewError] = useState<string | null>(initial.error);
  const [previewing, setPreviewing] = useState(false);
  const [confirmLarge, setConfirmLarge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Done | null>(null);
  const [tags, setTags] = useState<ContactTag[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [sequences, setSequences] = useState<{ id: string; name: string }[]>(initial.choices?.sequences ?? []);
  const [introducers, setIntroducers] = useState<{ id: string; name: string; firm: string | null }[]>(initial.choices?.introducers ?? []);
  const [introQuery, setIntroQuery] = useState("");
  const firstKey = useMemo(() => previewKey(initial.action, initial.params ?? {}), [initial]);
  const key = previewKey(action, params);
  const needs = missing({ action, params });
  const source = String(spec.source);

  // The choices each action needs: tags, stages, sequences.
  useEffect(() => {
    let alive = true;
    if (action === "tag_people" && !tags.length) crm.tagDefinitions().then((t) => alive && setTags(t ?? [])).catch(() => undefined);
    if (action === "set_stage" && !stages.length) crm.stages("lead").then((s) => alive && setStages(s ?? [])).catch(() => undefined);
    if (action === "enrol_in_sequence" && !sequences.length) crm.sequences().then((s) => alive && setSequences(s ?? [])).catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [crm, action, tags.length, stages.length, sequences.length]);

  // Re-run the preview (a read, as this person) whenever who-is-included could change.
  useEffect(() => {
    if (done) return;
    if (needs.length) {
      setPreview(null);
      return;
    }
    if (action === initial.action && key === firstKey && initial.preview) {
      setPreview(initial.preview);
      setPreviewError(initial.error);
      return;
    }
    let alive = true;
    setPreviewing(true);
    setPreviewError(null);
    crm
      .previewAction(spec, action, params)
      .then((p) => alive && setPreview(p))
      .catch((e) => {
        if (!alive) return;
        setPreview(null);
        setPreviewError((e as Error).message);
      })
      .finally(() => alive && setPreviewing(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- needs is compared by its joined text, not by identity
  }, [crm, spec, action, key, needs.join(",")]);

  useEffect(() => setConfirmLarge(false), [action, key]);

  // L-09 (item 85): an email to a list is for people she knows (ReConnect › Bulk emails); a cold list is refused.
  const [route, setRoute] = useState<EmailRoute | null>(null);
  useEffect(() => {
    setRoute(null);
    if (action !== "email" || !preview || previewing || !preview.included) return;
    let alive = true;
    sampleFirms(crm, preview)
      .then((f) => alive && setRoute(emailRoute(f)))
      .catch(() => alive && setRoute({ kind: "known" }));
    return () => {
      alive = false;
    };
  }, [crm, action, preview, previewing]);

  const set = (k: string, v: unknown) => setParams((p) => {
    const next = { ...p };
    if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) delete next[k];
    else next[k] = v;
    return next;
  });

  const switchTo = (a: ActionKind) => {
    setAction(a);
    setParams({});
    setPreview(null);
    setError(null);
  };

  const findIntroducers = async () => {
    if (!introQuery.trim()) return;
    try {
      const r = await crm.searchPeople({ query: introQuery.trim(), limit: 8 });
      setIntroducers(r.rows.map((x) => ({ id: x.id, name: x.name ?? "", firm: x.firm ?? null })));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const large = Boolean(preview?.limits.needs_large_confirm);
  const blocked = preview?.blocked ?? [];
  const emailReady = action !== "email" || (name.trim() && brief.trim());
  const taskReady = action !== "create_tasks" || (typeof params.title === "string" && params.title.trim());
  const coldList = route?.kind === "cold";
  const canConfirm = Boolean((action !== "email" || route?.kind === "known") && preview && !previewing && !blocked.length && preview.included > 0 && (!large || confirmLarge) && emailReady && taskReady && !busy);

  const confirm = async () => {
    if (!canConfirm) return;
    setBusy(true);
    setError(null);
    const p = { ...params, ...(large ? { confirm_large: true } : {}) };
    try {
      if (action === "email") setDone({ kind: "campaign", result: await crm.createCampaign(spec, p, name, brief) });
      else setDone({ kind: "bulk", result: await crm.runBulkAction(spec, action, p) });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const stageLabel = stages.find((s) => s.key === params.stage)?.label;
  const seqName = sequences.find((s) => s.id === params.sequence)?.name;
  const introName = introducers.find((s) => s.id === params.introducer)?.name;
  const reply: ActionReply = { ...initial, action, params, preview };

  if (done) {
    return (
      <section className="action-card is-done" aria-labelledby={`${id}-t`} aria-live="polite">
        <p className="action-eyebrow">
          <IconTile icon={CircleCheck} tone="moss" size="sm" />
          Done
        </p>
        {done.kind === "campaign" ? (
          <>
            <h2 id={`${id}-t`}>Campaign “{name.trim()}” started</h2>
            <p>
              The outreach agent is writing {fmtNumber(done.result.included)} personal {done.result.included === 1 ? "email" : "emails"}. They will wait in Campaigns › To approve
              for you to read, edit or reject before anything is sent.
              {done.result.excluded ? ` ${fmtNumber(done.result.excluded)} left out, as shown.` : ""}
            </p>
            <p className="action-links">
              <Link className="btn btn-primary" to="/campaigns/to-approve">See the drafts</Link>
              <Link className="btn" to={BULK_EMAILS_PATH}>See bulk emails</Link>
              <button type="button" className="btn btn-quiet" onClick={onClose}>Close</button>
            </p>
          </>
        ) : (
          <>
            <h2 id={`${id}-t`}>{ACTION_LABELS[action]}: {fmtNumber(done.result.done)} done</h2>
            <p>
              {done.result.failed ? `${fmtNumber(done.result.failed)} could not be changed. ` : ""}
              {done.result.excluded ? `${fmtNumber(done.result.excluded)} left out, as shown.` : ""}
            </p>
            {done.result.failed > 0 && (
              <ul className="action-failed">
                {done.result.results.filter((r) => !r.ok).slice(0, 20).map((r, i) => (
                  <li key={i}>{r.name ?? "Someone"}: {r.error}</li>
                ))}
              </ul>
            )}
            <p className="action-links">
              {action === "create_tasks" && <Link className="btn" to="/conversations/next-steps">See the tasks</Link>}
              <button type="button" className="btn btn-quiet" onClick={onClose}>Close</button>
            </p>
          </>
        )}
      </section>
    );
  }

  return (
    <section className="action-card" aria-labelledby={`${id}-t`}>
      <header className="action-head">
        <IconTile icon={Wand} tone="seal" size="lg" />
        <div className="action-head-text">
          <p className="action-eyebrow">Proposed action · nothing happens until you confirm</p>
          <h2 id={`${id}-t`}>{ACTION_LABELS[action]}</h2>
        </div>
        <label className="field field-inline action-switch">
          <span className="field-label">Action</span>
          <select value={action} onChange={(e) => switchTo(e.target.value as ActionKind)}>
            {ACTION_KINDS.map((a) => (
              <option key={a} value={a}>{ACTION_LABELS[a]}</option>
            ))}
          </select>
        </label>
      </header>

      {/* What each action needs */}
      <div className="action-fields">
        {(action === "email" || action === "tag_people" || action === "enrol_in_sequence" || action === "link_introducer") && source === "firms" && (
          <label className="check">
            <input type="checkbox" checked={params.all_contacts === true} onChange={(e) => set("all_contacts", e.target.checked || null)} />
            <span>Everyone at each firm, not just the main contact</span>
          </label>
        )}
        {action === "tag_people" && (
          <label className="field">
            <span className="field-label">Add the tag</span>
            <select value={Array.isArray(params.add) ? String((params.add as string[])[0] ?? "") : ""} onChange={(e) => set("add", e.target.value ? [e.target.value] : null)}>
              <option value="">Choose a tag</option>
              {tags.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
              {Array.isArray(params.add) && (params.add as string[]).filter((k) => !tags.some((t) => t.key === k)).map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
        )}
        {action === "enrol_in_sequence" && (
          <label className="field">
            <span className="field-label">Sequence</span>
            <select value={String(params.sequence ?? "")} onChange={(e) => set("sequence", e.target.value || null)}>
              <option value="">Choose a sequence</option>
              {sequences.map((s) => (
                <option key={s.id} value={s.id}>{s.name.replace(/_/g, " ")}</option>
              ))}
            </select>
          </label>
        )}
        {action === "set_stage" && (
          <>
            <label className="field">
              <span className="field-label">Move to</span>
              <select value={String(params.stage ?? "")} onChange={(e) => set("stage", e.target.value || null)}>
                <option value="">Choose a stage</option>
                {stages.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field-label">Reason</span>
              <input value={String(params.reason ?? "")} maxLength={500} onChange={(e) => set("reason", e.target.value)} placeholder="Moved from Ask" />
            </label>
          </>
        )}
        {action === "create_tasks" && (
          <>
            <label className="field">
              <span className="field-label">Task</span>
              <input value={String(params.title ?? "")} maxLength={200} onChange={(e) => set("title", e.target.value)} placeholder="Call about the event" />
            </label>
            <label className="field field-narrow">
              <span className="field-label">Due in (days)</span>
              <input type="number" min={0} max={365} value={String(params.due_in_days ?? 0)} onChange={(e) => set("due_in_days", Math.max(0, Math.min(365, Number(e.target.value) || 0)))} />
            </label>
            {source === "people" && (
              <label className="check">
                <input type="checkbox" checked={params.per === "firm"} onChange={(e) => set("per", e.target.checked ? "firm" : null)} />
                <span>One task per firm instead of per person</span>
              </label>
            )}
          </>
        )}
        {action === "link_introducer" && (
          <div className="field">
            <span className="field-label">Who introduced them</span>
            {introducers.length > 0 && (
              <select aria-label="Introducer" value={String(params.introducer ?? "")} onChange={(e) => set("introducer", e.target.value || null)}>
                <option value="">Choose a person</option>
                {introducers.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.firm ? `, ${p.firm}` : ""}</option>
                ))}
              </select>
            )}
            <span className="action-search">
              <input aria-label="Find the introducer" value={introQuery} onChange={(e) => setIntroQuery(e.target.value)} placeholder="Find by name"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void findIntroducers(); } }} />
              <button type="button" className="btn btn-quiet" onClick={() => void findIntroducers()}>Find</button>
            </span>
          </div>
        )}
      </div>

      {needs.length > 0 && <p className="muted">Choose the {needs.join(" and ")} to see who this applies to.</p>}
      {previewing && <p className="muted" role="status">Working out who this applies to…</p>}
      {previewError && <Notice text={previewError} tone="bad" />}

      {preview && !previewing && (
        <>
          <p className="action-what">{whatWillHappen(reply, { stage: stageLabel, sequence: seqName?.replace(/_/g, " "), introducer: introName })}</p>
          <div className="action-counts">
            <p className="action-count">
              <strong>{fmtNumber(preview.included)}</strong> included
            </p>
            <p className="action-count action-count-out">
              <strong>{fmtNumber(preview.excluded)}</strong> left out
            </p>
            <p className="action-count action-count-muted">
              from {fmtNumber(preview.matched)} {source === "firms" ? "firms" : "people"} on the list
              {preview.limited ? ` (the first ${fmtNumber(preview.matched)} of ${fmtNumber(preview.matched_total)} that match, in the list's order)` : ""}
            </p>
          </div>

          {preview.excluded > 0 && (
            <div className="action-exclusions">
              <h3>Left out, and why</h3>
              <ul>
                {groupExclusions(preview.exclusions, preview.by_reason).map((g) => (
                  <li key={g.reason}>
                    {/* card-check: not a card (a show-more of the names left out for one reason) */}
                    <details>
                      <summary>
                        <span className="action-reason">{g.label}</span> <span className="chip chip-plain">{fmtNumber(g.count)}</span>
                      </summary>
                      <ul className="action-names">
                        {g.rows.map((r, i) => (
                          <li key={`${r.person_id ?? r.organisation_id}-${i}`}>
                            {r.name ?? "No contact"}{r.firm && r.firm !== r.name ? <span className="muted"> · {r.firm}</span> : null}
                          </li>
                        ))}
                        {g.rows.length < g.count && <li className="muted">and {fmtNumber(g.count - g.rows.length)} more</li>}
                      </ul>
                    </details>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preview.sample.length > 0 && (
            <div className="action-sample">
              <h3>{preview.included > preview.sample.length ? `The first ${preview.sample.length} included` : "Included"}</h3>
              <ul>
                {preview.sample.map((r, i) => (
                  <li key={`${r.person_id ?? r.organisation_id}-${i}`}>
                    <span>{r.name}</span>
                    <span className="muted">{[r.firm !== r.name ? r.firm : null, r.email, r.nation ? NATION_LABELS[r.nation as Nation] ?? r.nation : null].filter(Boolean).join(" · ")}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {route && (
            <div className="action-route" role="status">
              <Notice text={emailRouteText(route)} tone={route.kind === "cold" ? "bad" : "ok"} />
              {route.kind === "cold" && (
                <p className="action-links">
                  <Link className="btn btn-primary" to={FIND_CAMPAIGNS_PATH}>Open Find campaigns</Link>
                </p>
              )}
            </div>
          )}

          {action === "email" && !coldList && (
            <div className="action-email">
              <label className="field">
                <span className="field-label">Campaign name</span>
                <input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
              </label>
              <label className="field action-wide">
                <span className="field-label">What the email is for</span>
                <textarea rows={3} value={brief} maxLength={4000} onChange={(e) => setBrief(e.target.value)} />
                <span className="field-hint">The outreach agent writes each email from this, your voice and each person's own hook.</span>
              </label>
              <label className="field">
                <span className="field-label">Suggested subject</span>
                <input value={String(params.subject ?? "")} maxLength={120} onChange={(e) => set("subject", e.target.value.replace(/[\r\n]+/g, " "))} />
              </label>
              <label className="field action-wide">
                <span className="field-label">Suggested opening line</span>
                <textarea rows={2} value={String(params.opening ?? "")} maxLength={600} onChange={(e) => set("opening", e.target.value)} />
              </label>
              {preview.sample[0] && (
                <div className="action-preview-email" aria-label="Sample email">
                  <p className="muted small">How the first one might start (the agent personalises each):</p>
                  <p><strong>{String(params.subject ?? name)}</strong></p>
                  <p>Dear {String(preview.sample[0].name ?? "").split(" ")[0] || "there"},</p>
                  <p>{String(params.opening ?? brief)}</p>
                </div>
              )}
            </div>
          )}

          {blocked.map((b) => (
            <Notice key={b.code} text={b.message} tone="bad" />
          ))}
          {large && !blocked.length && (
            <label className="check action-large">
              <input type="checkbox" checked={confirmLarge} onChange={(e) => setConfirmLarge(e.target.checked)} />
              <span>I've checked the list: go ahead with all {fmtNumber(preview.included)} (more than {preview.limits.large_threshold ?? 50})</span>
            </label>
          )}
        </>
      )}

      {error && <Notice text={error} tone="bad" />}
      <div className="action-buttons">
        {!coldList && (
          <button type="button" className="btn btn-primary" disabled={!canConfirm} onClick={() => void confirm()}>
            {busy ? "Confirming…" : action === "email" ? `Confirm: write ${fmtNumber(preview?.included ?? 0)} emails for approval` : `Confirm: ${ACTION_LABELS[action].toLowerCase()}`}
          </button>
        )}
        <button type="button" className="btn btn-quiet" onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>
    </section>
  );
}
